import {type DatesRangeValue} from '@mantine/dates'
import {format, isValid, parseISO} from 'date-fns'

import type {ProjectLifecycleStatus} from './schemas'

export type ProjectListSearch = {
    name?: string
    customer?: string
    from?: string
    to?: string
    status?: string
    sort?: string
    unbilled?: 'true'
}

export type ProjectSortField =
    | 'createdAt'
    | 'earliestStart'
    | 'jobCount'
    | 'name'
    | 'customerName'
    | 'status'

export type ProjectSortState = {
    field: ProjectSortField
    direction: 'asc' | 'desc'
}

const DEFAULT_SORT_STATE: ProjectSortState = {
    field: 'createdAt',
    direction: 'desc',
}

export const DEFAULT_PROJECT_STATUS_FILTER: ProjectLifecycleStatus[] = ['estimate', 'in_progress']

const PROJECT_LIFECYCLE_STATUSES = new Set<ProjectLifecycleStatus>([
    'estimate',
    'in_progress',
    'closed',
])

const VALID_SORT_VALUES = new Set<string>([
    'createdAt-desc',
    'createdAt-asc',
    'earliestStart-asc',
    'earliestStart-desc',
    'jobCount-asc',
    'jobCount-desc',
    'name-asc',
    'name-desc',
    'customerName-asc',
    'customerName-desc',
    'status-asc',
    'status-desc',
])

function parseTrimmedString(value: unknown): string | undefined {
    if (typeof value !== 'string') {
        return undefined
    }

    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
}

function parseDateSearchParam(value: unknown): string | undefined {
    const trimmed = parseTrimmedString(value)
    if (!trimmed) {
        return undefined
    }

    const parsed = parseISO(trimmed)
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : undefined
}

export function parseStatusSearchParam(value: unknown): ProjectLifecycleStatus[] {
    const values = Array.isArray(value) ? value : [value]

    return [
        ...new Set(
            values.flatMap((entry) => {
                const trimmed = parseTrimmedString(entry)
                return trimmed ? trimmed.split(',').map((status) => status.trim()) : []
            }),
        ),
    ].filter((entry): entry is ProjectLifecycleStatus =>
        PROJECT_LIFECYCLE_STATUSES.has(entry as ProjectLifecycleStatus),
    )
}

export function serializeStatusSearchParam(statuses: ProjectLifecycleStatus[]): string | undefined {
    const normalizedStatuses = [...new Set(statuses)].filter((status) =>
        PROJECT_LIFECYCLE_STATUSES.has(status),
    )
    return normalizedStatuses.length > 0 ? normalizedStatuses.join(',') : undefined
}

export function validateProjectListSearch(search: Record<string, unknown>): ProjectListSearch {
    const result: ProjectListSearch = {}

    const name = parseTrimmedString(search['name'])
    if (name) {
        result.name = name
    }

    const customer = parseTrimmedString(search['customer'])
    if (customer) {
        result.customer = customer
    }

    const from = parseDateSearchParam(search['from'])
    if (from) {
        result.from = from
    }

    const to = parseDateSearchParam(search['to'])
    if (to) {
        result.to = to
    }

    const status = serializeStatusSearchParam(parseStatusSearchParam(search['status']))
    if (status) {
        result.status = status
    }

    const sort = parseTrimmedString(search['sort'])
    if (sort && VALID_SORT_VALUES.has(sort)) {
        result.sort = sort
    }

    if (search['unbilled'] === 'true') {
        result.unbilled = 'true'
    }

    return result
}

export function sortStateToSearchParam(sortState: ProjectSortState): string | undefined {
    const value = `${sortState.field}-${sortState.direction}`
    if (value === `${DEFAULT_SORT_STATE.field}-${DEFAULT_SORT_STATE.direction}`) {
        return undefined
    }

    return value
}

export function searchToSortState(search: ProjectListSearch): ProjectSortState {
    if (!search.sort || !VALID_SORT_VALUES.has(search.sort)) {
        return DEFAULT_SORT_STATE
    }

    const separatorIndex = search.sort.lastIndexOf('-')
    const field = search.sort.slice(0, separatorIndex) as ProjectSortField
    const direction = search.sort.slice(separatorIndex + 1) as ProjectSortState['direction']
    return {field, direction}
}

export function searchToDateRange(search: ProjectListSearch): DatesRangeValue {
    const from = search.from ? parseISO(search.from) : null
    const to = search.to ? parseISO(search.to) : null

    return [from != null && isValid(from) ? from : null, to != null && isValid(to) ? to : null]
}

export function buildProjectListSearch(params: {
    name?: string
    customerId?: string | null
    scheduleDateRange: DatesRangeValue
    statusFilter: ProjectLifecycleStatus[]
    sortState: ProjectSortState
    hasUnbilled?: boolean
}): ProjectListSearch {
    const result: ProjectListSearch = {}

    const name = params.name?.trim()
    if (name) {
        result.name = name
    }

    if (params.customerId) {
        result.customer = params.customerId
    }

    const [fromDate, toDate] = params.scheduleDateRange
    if (fromDate) {
        result.from = format(fromDate, 'yyyy-MM-dd')
    }
    if (toDate) {
        result.to = format(toDate, 'yyyy-MM-dd')
    }

    const status = serializeStatusSearchParam(params.statusFilter)
    if (status) {
        result.status = status
    }

    const sort = sortStateToSearchParam(params.sortState)
    if (sort) {
        result.sort = sort
    }

    if (params.hasUnbilled) {
        result.unbilled = 'true'
    }

    return result
}

export function projectListSearchEquals(
    left: ProjectListSearch,
    right: ProjectListSearch,
): boolean {
    return (
        left.name === right.name &&
        left.customer === right.customer &&
        left.from === right.from &&
        left.to === right.to &&
        left.status === right.status &&
        left.sort === right.sort &&
        left.unbilled === right.unbilled
    )
}
